import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { OrderFormData } from "../types";

interface OrderTypeSectionProps {
  form: UseFormReturn<OrderFormData>;
}

export function OrderTypeSection({ form }: OrderTypeSectionProps) {
  return (
    <>
      <FormField
        control={form.control}
        name="order_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tilaustyyppi *</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={(value) => {
                  field.onChange(value);
                  if (value === "new_work" || value === "field_work") {
                    form.setValue("fulfillment_type", "installation");
                  } else if (value === "pickup_work") {
                    form.setValue("fulfillment_type", "pickup");
                  }
                }}
                value={field.value}
                className="flex flex-wrap gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="new_work" id="new_work" />
                  <FormLabel htmlFor="new_work" className="font-normal cursor-pointer">
                    🔨 Uusi työ
                  </FormLabel>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pickup_work" id="pickup_work" />
                  <FormLabel htmlFor="pickup_work" className="font-normal cursor-pointer">
                    🔄 Noudettava työ
                  </FormLabel>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="field_work" id="field_work" />
                  <FormLabel htmlFor="field_work" className="font-normal cursor-pointer">
                    🔧 Kenttätyö
                  </FormLabel>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {form.watch("order_type") === "field_work" ? (
        <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
          🔧 Työ suoritetaan paikan päällä hautausmaalla (toimitustapa: asennus)
        </div>
      ) : (
        <FormField
          control={form.control}
          name="fulfillment_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Toimitustapa *</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="flex gap-4">
                  {form.watch("order_type") === "new_work" ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="delivery" id="delivery" />
                        <FormLabel htmlFor="delivery" className="font-normal cursor-pointer">
                          Toimitus
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="installation" id="installation" />
                        <FormLabel htmlFor="installation" className="font-normal cursor-pointer">
                          Asennus
                        </FormLabel>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pickup" id="pickup" />
                        <FormLabel htmlFor="pickup" className="font-normal cursor-pointer">
                          📦 Nouto
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="return"
                          id="return"
                          disabled={form.watch("fulfillment_type") !== "return"}
                        />
                        <FormLabel htmlFor="return" className="font-normal cursor-pointer text-muted-foreground">
                          🔄 Palautus (automaattinen)
                        </FormLabel>
                      </div>
                    </>
                  )}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tila *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="draft">Luonnos</SelectItem>
                <SelectItem value="confirmed">Vahvistettu</SelectItem>
                <SelectItem value="ready_for_installation">Valmis asennukseen</SelectItem>
                <SelectItem value="installed">Asennettu</SelectItem>
                <SelectItem value="completed">Valmis</SelectItem>
                <SelectItem value="cancelled">Peruutettu</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Muistiinpanot</FormLabel>
            <FormControl>
              <Textarea {...field} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
